import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import {
  Trophy, Medal, Star, Crown, Flame, MessageCircle, Shield, Users, Scale, Lightbulb, Globe,
  Footprints, Award
} from 'lucide-react';

const ICON_MAP = { Footprints, Star, Flame, MessageCircle, Lightbulb, Users, Shield, Scale, Crown, Globe };

export default function LeaderboardPage() {
  const navigate = useNavigate();
  const { axiosAuth, user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [badges, setBadges] = useState([]);
  const [badgeStats, setBadgeStats] = useState({ earned: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const api = axiosAuth();
    Promise.all([api.get('/leaderboard'), api.get('/badges')])
      .then(([lbRes, bgRes]) => { setLeaderboard(lbRes.data.leaderboard); setBadges(bgRes.data.badges); setBadgeStats({ earned: bgRes.data.earned_count, total: bgRes.data.total_count }); })
      .catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, [axiosAuth]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heritage-card p-8 text-center"><div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="font-body text-sm">Loading...</p></div>
    </div>
  );

  const RankDisplay = ({ rank }) => {
    if (rank === 1) return <div className="w-8 h-8 bg-warning rounded-full flex items-center justify-center"><Crown className="w-4 h-4 text-white" /></div>;
    if (rank === 2) return <div className="w-8 h-8 bg-muted-foreground/60 rounded-full flex items-center justify-center"><Medal className="w-4 h-4 text-white" /></div>;
    if (rank === 3) return <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center"><Medal className="w-4 h-4 text-white" /></div>;
    return <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center font-heading text-sm font-bold text-muted-foreground">{rank}</div>;
  };

  const currentUserId = user?.user_id || user?.id;

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard-page">
      <Navbar showBack backTo="/" backLabel="Leaderboard & Badges" />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <Tabs defaultValue="leaderboard" className="space-y-5">
          <TabsList className="border border-border bg-surface p-1 shadow-soft" data-testid="lb-tabs">
            <TabsTrigger value="leaderboard" className="font-heading font-semibold data-[state=active]:bg-brand data-[state=active]:text-[#333333] rounded-md px-4 text-sm">Leaderboard</TabsTrigger>
            <TabsTrigger value="badges" className="font-heading font-semibold data-[state=active]:bg-brand data-[state=active]:text-[#333333] rounded-md px-4 text-sm">
              Badges ({badgeStats.earned}/{badgeStats.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leaderboard">
            <div className="mb-6">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Rankings</h1>
              <p className="font-body text-sm text-muted-foreground mt-1">Top performers ranked by average score</p>
            </div>

            {leaderboard.length === 0 ? (
              <div className="heritage-card p-10 text-center">
                <Trophy className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
                <p className="font-heading text-base font-bold mb-1">No rankings yet</p>
                <p className="font-body text-sm text-muted-foreground mb-4">Complete simulations to appear</p>
                <Button onClick={() => navigate('/')} className="bg-brand text-[#333333] font-medium rounded-lg h-9 px-4 text-sm hover:opacity-90">Start Practicing</Button>
              </div>
            ) : (
              <div className="space-y-2" data-testid="leaderboard-list">
                {leaderboard.map((entry, i) => {
                  const isMe = entry.user_id === currentUserId;
                  return (
                    <div key={entry.user_id}
                      className={`heritage-card p-3 sm:p-4 flex items-center gap-3 sm:gap-4 ${isMe ? 'ring-2 ring-brand bg-brand/5' : ''}`}
                      data-testid={`lb-entry-${i}`}>
                      <RankDisplay rank={entry.rank} />
                      <div className="w-9 h-9 bg-brand text-[#333333] rounded-full flex items-center justify-center font-heading font-bold text-xs flex-shrink-0">
                        {entry.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-heading text-sm font-bold truncate">{entry.name}</p>
                          {isMe && <Badge className="bg-brand/10 text-brand border-none font-body text-[10px] px-1.5 py-0">You</Badge>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground font-body">
                          {entry.team && <span>{entry.team}</span>}
                          <span>{entry.completed_count} sims</span>
                          <span className="flex items-center gap-0.5"><Award className="w-3 h-3" /> {entry.badge_count}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-heading text-xl sm:text-2xl font-bold">{entry.avg_score}</p>
                        <p className="font-body text-[10px] text-muted-foreground">avg</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="badges">
            <div className="mb-6">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Your Badges</h1>
              <p className="font-body text-sm text-muted-foreground mt-1">Earned {badgeStats.earned} of {badgeStats.total}</p>
              <div className="mt-3 h-2 bg-muted rounded-full overflow-hidden max-w-md">
                <div className="h-full bg-brand rounded-full transition-all duration-700" style={{ width: `${(badgeStats.earned / badgeStats.total) * 100}%` }} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="badges-grid">
              {badges.map((badge) => {
                const IconComp = ICON_MAP[badge.icon] || Award;
                return (
                  <div key={badge.id} className={`heritage-card p-4 transition-all ${badge.earned ? '' : 'opacity-35 grayscale'}`} data-testid={`badge-${badge.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: badge.earned ? `${badge.color}15` : 'var(--muted)' }}>
                        <IconComp className="w-5 h-5" style={{ color: badge.earned ? badge.color : 'var(--muted-fg)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="font-heading text-sm font-bold">{badge.name}</p>
                          {badge.earned && <Badge className="bg-success/10 text-success border-none font-body text-[10px] px-1.5 py-0">Earned</Badge>}
                        </div>
                        <p className="font-body text-xs text-muted-foreground mt-0.5">{badge.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
